import { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { PaginatorType } from '@econtractor/types';
import { useTask, TASKS_PAGE_SIZE_KEY } from '../TaskContext';
import SpinnerBlock from 'components/others/SpinnerBlock';
import TableHeadTdOrder from 'components/others/TableHeadTdOrder';
import Paginator from 'components/others/Paginator';
import ESpinner from 'components/others/Spinner';
import TaskCard from '../TaskCard';
import TaskTableBody from './TaskTableBody';

const PaginatorAny = Paginator as React.ComponentType<{
    paginator: PaginatorType | null;
    storageKey?: string;
    onChangePage?: (page: number) => void;
    onChangeOnPage?: (onPage: number) => void;
}>;

export interface ShowTaskTableProps {
    leadId?: number | null;
    objectId?: number | null;
}

const ShowTaskTable = ({ leadId = null, objectId = null }: ShowTaskTableProps) => {
    const { t } = useTranslation(['Tasks']);
    const {
        tasks,
        paginator,
        showSpinner,
        tasksOrder,
        setTasksOrder,
        tasksDesc,
        setTasksDesc,
        setPage,
        setOnPage,
        leadId: ctxLeadId,
        objectId: ctxObjectId,
    } = useTask();
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);

    const handleOrderClick = (value: string, desc: boolean) => {
        setTasksOrder(value);
        setTasksDesc(desc);
        setPage(1);
    };

    if (tasks === null) {
        return (
            <div className="py-5">
                <ESpinner />
            </div>
        );
    }

    return (
        <div
            className="show-task-table d-flex flex-column"
            data-lead-id={leadId ?? ctxLeadId ?? undefined}
            data-object-id={objectId ?? ctxObjectId ?? undefined}
        >
            <div className="show-task-table-content px-3 position-relative">
                <SpinnerBlock onlyBlur={true} show={showSpinner} />

                <div className="crm-table-wrap">
                    <Table className="TaskTable table-hover mb-0 mt-1">
                        <colgroup>
                            <col className="task-col-name" />
                            <col className="task-col-status" style={{ width: 130 }} />
                            <col className="task-col-term" style={{ width: 150 }} />
                            <col className="task-col-executor" style={{ width: 220 }} />
                            <col className="task-col-controller" style={{ width: 220 }} />
                            <col className="task-col-actions" style={{ width: 64 }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-light">
                                <td className="head-name">
                                    <TableHeadTdOrder
                                        title={t('fields.name')}
                                        onClick={handleOrderClick}
                                        value={tasksOrder}
                                        desc={tasksDesc}
                                        fieldName="v_tasks.task_name"
                                    />
                                </td>
                                <td className="head-status">
                                    <TableHeadTdOrder
                                        title={t('fields.status')}
                                        onClick={handleOrderClick}
                                        value={tasksOrder}
                                        desc={tasksDesc}
                                        fieldName="v_tasks.task_status"
                                    />
                                </td>
                                <td className="head-term">
                                    <TableHeadTdOrder
                                        title={t('fields.term')}
                                        onClick={handleOrderClick}
                                        value={tasksOrder}
                                        desc={tasksDesc}
                                        fieldName="v_tasks.date_end"
                                    />
                                </td>
                                <td className="head-executor">
                                    <TableHeadTdOrder
                                        title={t('fields.executor')}
                                        onClick={handleOrderClick}
                                        value={tasksOrder}
                                        desc={tasksDesc}
                                        fieldName="executor_user.user_name"
                                    />
                                </td>
                                <td className="head-controller">
                                    <TableHeadTdOrder
                                        title={t('fields.controller')}
                                        onClick={handleOrderClick}
                                        value={tasksOrder}
                                        desc={tasksDesc}
                                        fieldName="controller_user.user_name"
                                    />
                                </td>
                                <td className="head-actions" aria-label={t('btnRemind')} />
                            </tr>
                        </thead>

                        <TaskTableBody tasks={tasks} onOpenTask={setOpenTaskId} />
                    </Table>
                </div>
            </div>
 
            {paginator && (
                <div className="p-3 offcanvas-footer show-task-table-footer">
                    <PaginatorAny
                        paginator={paginator}
                        storageKey={TASKS_PAGE_SIZE_KEY}
                        onChangePage={(nextPage) => setPage(nextPage)}
                        onChangeOnPage={(nextOnPage) => {
                            setOnPage(nextOnPage);
                            setPage(1);
                        }}
                    />
                </div>
            )}

            <TaskCard taskId={openTaskId} onHide={() => setOpenTaskId(null)} />
        </div>
    );
};

export default ShowTaskTable;
